import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { showToast, startLoader, stopLoader, open_dialog } from "../../../lib/global";
import { COLLECTION_ADD_PRODUCT_DIALOG } from "../../../lib/constant";
import { breadcrumbAction } from "../../../redux/actions/config.action";
import {
  createCollection,
  getCollectionById,
  getCollectionProducts,
  updateCollectionById,
  updateCollectionProducts,
} from "../../../redux/actions/collections.action";
import Image from "../../../components/Images/Image";
import { formatProductPriceLabel } from "../../../utils/productPrice";

const mapApiProductToDialog = (product) => ({
  productId: product.product_id,
  title: product.title,
  productImages: product.product_images,
  price: product.price,
  display_price: product.display_price,
  totalStock: product.total_stock,
});

function AddCollection() {
  const { collectionId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const [name, setName] = useState("");
  const [assignedProducts, setAssignedProducts] = useState([]);

  const fetchAssignedProducts = useCallback(() => {
    if (!collectionId) return Promise.resolve();

    return dispatch(getCollectionProducts(collectionId)).then((res) => {
      if (res?.data?.status === 200) {
        setAssignedProducts((res.data.data || []).map(mapApiProductToDialog));
      }
    });
  }, [collectionId, dispatch]);

  useEffect(() => {
    dispatch(
      breadcrumbAction([
        { label: "Products", url: "/products/all-products" },
        { label: "Collections", url: "/products/collections" },
        { label: collectionId ? "Edit Collection" : "Add Collection", url: null },
      ])
    );

    if (collectionId) {
      startLoader();
      Promise.all([
        dispatch(getCollectionById(collectionId)),
        fetchAssignedProducts(),
      ])
        .then(([collectionRes]) => {
          stopLoader();
          if (collectionRes?.data?.status === 200) {
            setName(collectionRes.data.data.name || "");
          }
        })
        .catch(() => {
          stopLoader();
          showToast("Failed to fetch collection", "error");
        });
    }
  }, [collectionId, dispatch, fetchAssignedProducts]);

  const handleManageProducts = () => {
    open_dialog(COLLECTION_ADD_PRODUCT_DIALOG, {
      initialSelected: assignedProducts,
      onSubmit: async (selectedProducts) => {
        if (!collectionId) {
          setAssignedProducts(selectedProducts);
          return;
        }

        startLoader();
        try {
          const productIds = selectedProducts.map((p) => p.productId);
          const res = await dispatch(updateCollectionProducts(collectionId, productIds));
          stopLoader();
          if (res?.data?.status === 200) {
            showToast("Collection products updated successfully", "success");
            await fetchAssignedProducts();
          } else {
            showToast(res?.data?.message || "Failed to update products", "error");
          }
        } catch {
          stopLoader();
          showToast("Failed to update products", "error");
        }
      },
    });
  };

  const onSubmit = async () => {
    if (!name.trim()) {
      showToast("Collection name is required", "error");
      return;
    }

    startLoader();
    try {
      const payload = { name: name.trim() };

      if (collectionId) {
        const res = await dispatch(updateCollectionById(collectionId, payload));
        stopLoader();
        if (res?.data?.status === 200) {
          showToast("Collection updated successfully", "success");
          history.push("/products/collections");
        } else {
          showToast(res?.data?.message || "Failed to update collection", "error");
        }
      } else {
        const res = await dispatch(createCollection(payload));
        if (res?.data?.status !== 201) {
          stopLoader();
          showToast(res?.data?.message || "Failed to create collection", "error");
          return;
        }

        const newCollectionId = res.data.data?.collection_id;
        if (assignedProducts.length > 0 && newCollectionId) {
          const productIds = assignedProducts.map((p) => p.productId);
          const productsRes = await dispatch(updateCollectionProducts(newCollectionId, productIds));
          stopLoader();
          if (productsRes?.data?.status !== 200) {
            showToast(
              productsRes?.data?.message || "Collection created but failed to assign products",
              "error"
            );
            return;
          }
        } else {
          stopLoader();
        }

        showToast("Collection created successfully", "success");
        history.push("/products/collections");
      }
    } catch {
      stopLoader();
      showToast("Something went wrong", "error");
    }
  };

  return (
    <div className="card mx-3 p-4 shadow-sm border-0">
      <h5 className="font-weight-bold mb-4">
        {collectionId ? "Edit Collection" : "Add Collection"}
      </h5>

      <div className="row">
        <div className="col-md-8">
          <div className="form-group">
            <label>Collection Name *</label>
            <input
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Fiction, Staff Picks"
            />
          </div>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-md-10">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="font-weight-bold mb-1">Products in Collection</h6>
              <p className="text-muted small mb-0">
                {assignedProducts.length} product{assignedProducts.length === 1 ? "" : "s"} assigned
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={handleManageProducts}
            >
              Manage Products
            </button>
          </div>

          {assignedProducts.length === 0 ? (
            <p className="text-muted small border rounded p-3 mb-0">
              No products assigned yet. Click &quot;Manage Products&quot; to add products to this collection.
            </p>
          ) : (
            <div className="border rounded">
              {assignedProducts.map((product) => (
                <div
                  key={product.productId}
                  className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                >
                  <div className="d-flex align-items-center">
                    <Image
                      src={product.productImages?.[0] || "/icons/upload_icon.svg"}
                      alt="product"
                      style={{ width: 40, height: 40, objectFit: "cover", marginRight: 12 }}
                    />
                    <span>{product.title}</span>
                  </div>
                  <span className="text-muted small">{formatProductPriceLabel(product)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-light mr-2"
          onClick={() => history.push("/products/collections")}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-dark" onClick={onSubmit}>
          Save Collection
        </button>
      </div>
    </div>
  );
}

export default AddCollection;
